import { css } from '@emotion/react';
import { MouseEventHandler } from 'react';
import { getColor } from '../foundations';

type ButtonSizes = 'large' | 'small';
export type ButtonColors =
  | 'darkblue'
  | 'yellow'
  | 'green'
  | 'lightblue'
  | 'white'
  | 'gray'
  | 'disabled';
export type ButtonTypes = 'primary' | 'secondary';
type InputTypes = 'button' | 'submit' | 'reset';

interface ButtonStyle {
  textColor: string;
  buttonBackgroundColor: string;
  buttonHoverColor: string;
  buttonFocusColor: string;
  border: string;
}

interface ButtonStyles {
  primary: {
    darkblue: ButtonStyle;
    yellow: ButtonStyle;
    green: ButtonStyle;
    lightblue: ButtonStyle;
    gray: ButtonStyle;
    disabled: ButtonStyle;
  };
  secondary: {
    darkblue: ButtonStyle;
    yellow: ButtonStyle;
    green: ButtonStyle;
    white: ButtonStyle;
    disabled: ButtonStyle;
  };
}

const buttonStyles: ButtonStyles = {
  primary: {
    // default styles
    darkblue: {
      textColor: getColor('n0'),
      buttonBackgroundColor: getColor('b400'),
      buttonHoverColor: '#0047B3',
      buttonFocusColor: '#004099',
      border: 'none'
    },
    yellow: {
      textColor: getColor('n800'),
      buttonBackgroundColor: getColor('y300'),
      buttonHoverColor: getColor('y400'),
      buttonFocusColor: getColor('y500'),
      border: 'none'
    },
    green: {
      textColor: getColor('n800'),
      buttonBackgroundColor: getColor('g100'),
      buttonHoverColor: getColor('g200'),
      buttonFocusColor: getColor('g300'),
      border: 'none'
    },
    lightblue: {
      textColor: getColor('b400'),
      buttonBackgroundColor: getColor('b50'),
      buttonHoverColor: '#B3D4FF',
      buttonFocusColor: getColor('b100'),
      border: 'none'
    },
    gray: {
      textColor: getColor('n800'),
      buttonBackgroundColor: getColor('n30'),
      buttonHoverColor: '#DFE1E6',
      buttonFocusColor: getColor('n50'),
      border: 'none'
    },
    disabled: {
      textColor: getColor('n80'),
      buttonBackgroundColor: getColor('n30'),
      buttonHoverColor: '',
      buttonFocusColor: '',
      border: 'none'
    }
  },
  secondary: {
    darkblue: {
      textColor: getColor('b400'),
      buttonBackgroundColor: 'transparent',
      buttonHoverColor: getColor('n30'),
      buttonFocusColor: getColor('b50'),
      border: `0.063rem solid ${getColor('b400')}`
    },
    yellow: {
      textColor: getColor('n800'),
      buttonBackgroundColor: 'transparent',
      buttonHoverColor: getColor('n20'),
      buttonFocusColor: '#FFF0B3',
      border: `0.063rem solid ${getColor('y300')}`
    },
    green: {
      textColor: getColor('n800'),
      buttonBackgroundColor: 'transparent',
      buttonHoverColor: getColor('n20'),
      buttonFocusColor: getColor('g50'),
      border: `0.063rem solid ${getColor('g100')}`
    },
    white: {
      textColor: getColor('n0'),
      buttonBackgroundColor: 'transparent',
      buttonHoverColor: 'rgba(255, 255, 255, 0.10)',
      buttonFocusColor: 'rgba(255, 255, 255, 0.20)',
      border: `0.063rem solid ${getColor('n0')}`
    },
    disabled: {
      textColor: getColor('n80'),
      buttonBackgroundColor: getColor('n30'),
      buttonHoverColor: '',
      buttonFocusColor: '',
      border: 'none'
    }
  }
};

const getButtonSize = (size: string | undefined): string => {
  if (size) {
    const buttonSize = size.toLowerCase();
    return buttonSize ? buttonSize : 'large';
  }

  return 'large';
};

const getButtonType = (type: string | undefined): string => {
  const buttonType = type?.toLowerCase();
  if (buttonType) {
    return buttonType;
  }

  return 'primary';
};

const getButtonColor = (
  color: string | undefined,
  disabled: boolean | undefined
): string => {
  const buttonColor = color?.toLowerCase();
  if (disabled) {
    return 'disabled';
  }
  if (buttonColor) {
    return buttonColor;
  }

  return 'darkblue';
};

const getButtonStyles = (color: string, type: string): ButtonStyle => {
  const buttonTypeStyles = buttonStyles[type as keyof ButtonStyles];
  const styles: ButtonStyle =
    buttonTypeStyles[color as keyof typeof buttonTypeStyles];

  if (styles) {
    return styles;
  }

  // return darkblue primary styles if style was not found
  return buttonStyles.primary.darkblue;
};

export interface ButtonProps {
  text: string;
  type?: ButtonTypes;
  color?: ButtonColors;
  size?: ButtonSizes;
  onClick?: MouseEventHandler;
  url?: string;
  inputType?: InputTypes;
  disabled?: boolean;
  className?: string;
}

export const Button = ({
  text,
  type,
  color,
  size,
  onClick,
  url,
  inputType,
  disabled,
  className
}: ButtonProps) => {
  const buttonType: string = getButtonType(type);
  const buttonColor: string = getButtonColor(color, disabled);
  const {
    textColor,
    buttonBackgroundColor,
    buttonHoverColor,
    buttonFocusColor,
    border
  } = getButtonStyles(buttonColor, buttonType);
  const buttonSize: string = getButtonSize(size);
  const buttonHeight = buttonSize === 'large' ? 2.5 : 2;
  const fontSize = buttonSize === 'large' ? 1 : 0.875;
  const cursor = disabled ? 'cursor' : 'pointer';

  const serializedStyles = css`
    align-items: center;
    background: ${buttonBackgroundColor};
    border-radius: 0.25rem;
    box-sizing: border-box;
    border: ${border};
    cursor: ${cursor};
    color: ${textColor};
    display: inline-flex;
    font-weight: 600;
    font-size: ${fontSize}rem;
    font-family: var(--bxp-charlie-text-font-family);
    height: ${buttonHeight}rem;
    line-height: 1.25rem;
    padding: 0 1rem;
    text-align: center;
    text-decoration: none;
    :hover,
    :focus {
      background: ${buttonHoverColor};
    }
    :active {
      background: ${buttonFocusColor};
    }
  `;

  if ((onClick || inputType) && !url) {
    return (
      <button
        className={className}
        css={serializedStyles}
        onClick={onClick}
        disabled={disabled}
        type={inputType}
      >
        {text}
      </button>
    );
  }

  return (
    <a
      className={className}
      css={serializedStyles}
      onClick={onClick}
      href={url}
    >
      {text}
    </a>
  );
};
